import { useMemo } from "react";
import { type CellContext, type ColumnDef } from "@tanstack/react-table";
import { CheckCircleIcon, ShareIcon, Users02Icon } from "vega-icons";

import { getFullName } from "@vega-api/custom/utils/user";
import { ActionMenu } from "@vega-core/components/ActionMenu/ActionMenu";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ClientAvatar } from "@vega-core/components/ClientAvatar/ClientAvatar";
import { TABLE_COLUMN_WIDTHS } from "@vega-core/components/Table/CoreTable/constants";
import { m } from "@vega-core/paraglide/messages";

import { type ProspectWithMemberCount } from "./useOutreachManagement";

type UseOutreachTableColumnsParams = {
  onShare?: (rowIndex: number) => void;
  onCopyLink?: (rowIndex: number) => void;
  onAddOpportunityOwner?: (prospectIds: string[]) => void;
  onUpdateDataPoints?: (rowIndex: number) => void;
  onOpenContactsModal?: (rowIndex: number) => void;
};

export const useOutreachTableColumns = ({
  onShare,
  onCopyLink,
  onAddOpportunityOwner,
  onUpdateDataPoints,
  onOpenContactsModal,
}: UseOutreachTableColumnsParams): ColumnDef<
  ProspectWithMemberCount,
  unknown
>[] => {
  const columns = useMemo(() => {
    const limitedPartnerColumn: ColumnDef<ProspectWithMemberCount, unknown> = {
      id: "limitedPartner",
      maxSize: 420,
      header: m.coInvestments_prospects_limitedPartner(),
      accessorFn: (row) => row.limitedPartner.name,
      cell: (info: CellContext<ProspectWithMemberCount, unknown>) => {
        const item = info.row.original;

        return (
          <span className="body-base-medium truncate">
            {item.limitedPartner.name || ""}
          </span>
        );
      },
    };

    const relationshipColumn: ColumnDef<ProspectWithMemberCount, unknown> = {
      id: "relationship",
      header: m.coInvestments_prospects_opportunityOwner(),
      accessorFn: (row) =>
        row.relationship ? getFullName(row.relationship.fullName) : "",
      cell: (info: CellContext<ProspectWithMemberCount, unknown>) => {
        const item = info.row.original;

        if (!item.relationship) {
          return <span className="body-base-regular">&mdash;</span>;
        }

        const fullName = getFullName(item.relationship.fullName);

        return (
          <div className="flex flex-col">
            <span className="body-base-medium">{fullName}</span>
            <span className="body-small-regular text-text/secondary">
              {item.relationship.email}
            </span>
          </div>
        );
      },
    };

    const contactsColumn: ColumnDef<ProspectWithMemberCount, unknown> = {
      id: "contacts",
      header: m.coInvestments_prospects_contacts(),
      size: TABLE_COLUMN_WIDTHS.DEFAULT,
      maxSize: TABLE_COLUMN_WIDTHS.DEFAULT,
      accessorKey: "memberCount",
      cell: (info: CellContext<ProspectWithMemberCount, unknown>) => {
        const item = info.row.original;
        const memberCount = item.memberCount ?? 0;
        const rowIndex = info.row.index;

        return (
          <ThemedButton
            dataTestId={`contacts-button-${rowIndex}`}
            icon={<Users02Icon className="w-4 h-4" />}
            iconPosition="left"
            onClick={() => onOpenContactsModal?.(rowIndex)}
            variant="text"
          >
            {String(memberCount)}
          </ThemedButton>
        );
      },
    };

    const tierColumn: ColumnDef<ProspectWithMemberCount, unknown> = {
      id: "tier",
      header: m.coInvestments_prospects_tier(),
      size: TABLE_COLUMN_WIDTHS.DEFAULT,
      maxSize: TABLE_COLUMN_WIDTHS.DEFAULT,
      accessorKey: "tier",
      cell: (info: CellContext<ProspectWithMemberCount, unknown>) => {
        const item = info.row.original;

        return (
          <ClientAvatar
            isBold
            name={item.tier || ""}
            size="small"
            variant="primaryBackground"
          />
        );
      },
    };

    const actionsColumn: ColumnDef<ProspectWithMemberCount, unknown> = {
      id: "actions",
      addCellTooltip: false,
      enableSorting: false,
      cell: ({
        row: {
          index: rowIndex,
          original: {
            id: prospectId,
            isDealSent,
            isPermissionLocked,
            canSendDeal,
          },
        },
      }: CellContext<ProspectWithMemberCount, unknown>) => {
        const isShared = isDealSent && !isPermissionLocked;

        const actionMenuItems: {
          title: string;
          onClick: () => void;
        }[] = [];

        if (onUpdateDataPoints) {
          actionMenuItems.push({
            title: m.coInvestments_outreach_updateDataPoints(),
            onClick: () => onUpdateDataPoints(rowIndex),
          });
        }

        if (onCopyLink) {
          actionMenuItems.push({
            title: m.btn_copyLink(),
            onClick: () => onCopyLink(rowIndex),
          });
        }

        if (onAddOpportunityOwner) {
          actionMenuItems.push({
            title: m.btn_addOpportunityOwner(),
            onClick: () => {
              onAddOpportunityOwner([prospectId]);
            },
          });
        }

        return (
          <div className="flex items-center gap-3 justify-end">
            {isShared ? (
              <div
                className="flex items-center gap-1.5 text-text/positive body-base-medium"
                data-testid={`shared-status-${rowIndex}`}
              >
                <CheckCircleIcon className="w-4.5 h-4.5" />
                <span>{m.coInvestments_share_shared()}</span>
              </div>
            ) : null}
            {canSendDeal && onShare ? (
              <ThemedButton
                className="min-h-7 h-9 px-3"
                dataTestId={`share-button-${rowIndex}`}
                icon={<ShareIcon className="size-5" />}
                iconPosition="right"
                onClick={() => onShare(rowIndex)}
                size="small"
                variant={isShared ? "secondary" : "primary"}
              >
                {m.btn_share()}
              </ThemedButton>
            ) : null}
            {canSendDeal && actionMenuItems.length > 0 ? (
              <ActionMenu
                className="[&>button]:min-h-7 [&>button]:size-9"
                menuItems={actionMenuItems}
              />
            ) : null}
          </div>
        );
      },
    };

    return [
      limitedPartnerColumn,
      relationshipColumn,
      contactsColumn,
      tierColumn,
      actionsColumn,
    ];
  }, [
    onShare,
    onCopyLink,
    onAddOpportunityOwner,
    onUpdateDataPoints,
    onOpenContactsModal,
  ]);

  return columns;
};
